import React, { useState, useEffect } from 'react';

import { YellowButton } from 'components/Buttons/Buttons';
import { BugTracker } from 'kb-shared/utilities/bugTracker';
import styled from 'styled-components';
import { showErrorToast } from 'utilities/notificationUtils';

import Field from './Field';
import RiskAssessmentFormType from '../types/RiskAssessmentForm';

interface Props {
  form: RiskAssessmentFormType;
  onSubmit: () => void;
  onSaveAnswer: (fieldId: number, answer: boolean) => Promise<any>;
}

const RiskAssessmentForm = ({ form, onSaveAnswer, onSubmit }: Props): React.JSX.Element => {
  const [savingFields, setSavingFields] = useState<Set<number>>(new Set());
  const [confirmedAnswers, setConfirmedAnswers] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (!form?.fields) return;

    const initialAnswers: Record<number, boolean> = {};
    form.fields.forEach(field => {
      if (field.answer != null) initialAnswers[field.id] = field.answer;
    });
    setConfirmedAnswers(initialAnswers);
  }, [form?.fields]);

  const handleSaveAnswer = async (fieldId: number, answer: boolean) => {
    setSavingFields(prev => new Set(prev).add(fieldId));

    try {
      await onSaveAnswer(fieldId, answer);
      setConfirmedAnswers(prev => ({ ...prev, [fieldId]: answer }));
      setSavingFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(fieldId);
        return newSet;
      });
    } catch (error) {
      BugTracker.notify(error, 'RiskAssessmentFormSaveAnswer');
      showErrorToast('Failed to save answer. Please try again.');
      setSavingFields(prev => {
        const newSet = new Set(prev);
        newSet.delete(fieldId);
        return newSet;
      });
    }
  };

  return (
    <Form
      onSubmit={e => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <Header>{form.title}</Header>

      <Description>Please select Yes or No if you have any condition below.</Description>

      <Fields>
        {form.fields.map(field => (
          <Field
            key={field.id}
            field={{
              ...field,
              answer: confirmedAnswers[field.id]
            }}
            onChange={answer => handleSaveAnswer(field.id, answer)}
            isSaving={savingFields.has(field.id)}
          />
        ))}
      </Fields>

      <ConfirmButton>CONFIRM</ConfirmButton>
    </Form>
  );
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.colors.neutral.primaryNavy};
`;

const Header = styled.h2`
  margin-bottom: 8px;

  font-size: 24px;
  line-height: 32px;

  @media ${({ theme }) => theme.queries.minTablet} {
    font-size: 28px;
    line-height: 37px;
  }
`;

const Description = styled.p`
  margin-bottom: 23px;

  font-size: 16px;
  line-height: 20px;

  @media ${({ theme }) => theme.queries.minTablet} {
    margin-bottom: 32px;
  }
`;

const Fields = styled.div`
  margin: 0px -16px 32px -16px;

  @media ${({ theme }) => theme.queries.minTablet} {
    margin: 0px 0px 32px 0px;
  }
`;

const ConfirmButton = styled(YellowButton)`
  align-self: center;
`;

export default RiskAssessmentForm;
